import type { StudioIconName } from './components/StudioIcons';

export type Locale = 'zh' | 'en';

export const LOCALE_STORAGE_KEY = 'studio.locale';
export const APP_BRAND_NAME = '蓦浮 Morpho';

const SECTION_STYLE: Record<string, { icon: StudioIconName; accent: string; tint: string }> = {
  world_state: { icon: 'world', accent: '#0f8a6a', tint: 'rgba(16, 163, 127, 0.12)' },
  theme: { icon: 'theme', accent: '#a27431', tint: 'rgba(177, 153, 108, 0.14)' },
  character: { icon: 'character', accent: '#9a5b49', tint: 'rgba(154, 91, 73, 0.12)' },
  arc_milestone: { icon: 'arc', accent: '#6e7b91', tint: 'rgba(110, 123, 145, 0.12)' },
  scene: { icon: 'scene', accent: '#3f6f8d', tint: 'rgba(63, 111, 141, 0.12)' },
  event: { icon: 'event', accent: '#2f7f68', tint: 'rgba(47, 127, 104, 0.12)' },
  plot_device: { icon: 'device', accent: '#8d6a3f', tint: 'rgba(141, 106, 63, 0.12)' },
  dialogue: { icon: 'dialogue', accent: '#8d4f5f', tint: 'rgba(141, 79, 95, 0.12)' },
};

export const META_BY_LOCALE: Record<
  Locale,
  Record<string, { label: string; desc: string; icon: StudioIconName }>
> = {
  zh: {
    world_state: { label: '世界观', desc: '题材、规则与环境', icon: 'world' },
    theme: { label: '主题', desc: '命题与表达方向', icon: 'theme' },
    character: { label: '人物', desc: '角色目标、动机与弧线', icon: 'character' },
    arc_milestone: { label: '人物弧光', desc: '关键转变节点', icon: 'arc' },
    scene: { label: '场景', desc: '可拍摄的戏剧单元', icon: 'scene' },
    event: { label: '事件', desc: '动作、转折与推进', icon: 'event' },
    plot_device: { label: '戏剧装置', desc: '伏笔、误导与回收', icon: 'device' },
    dialogue: { label: '对白', desc: '台词、潜台词与情绪', icon: 'dialogue' },
  },
  en: {
    world_state: { label: 'World', desc: 'Premise, rules, and setting', icon: 'world' },
    theme: { label: 'Theme', desc: 'Ideas and thematic direction', icon: 'theme' },
    character: { label: 'Character', desc: 'Goals, motives, and arcs', icon: 'character' },
    arc_milestone: { label: 'Arc', desc: 'Key turning beats', icon: 'arc' },
    scene: { label: 'Scene', desc: 'Shootable dramatic units', icon: 'scene' },
    event: { label: 'Event', desc: 'Actions and reversals', icon: 'event' },
    plot_device: { label: 'Device', desc: 'Foreshadowing, misdirection, payoff', icon: 'device' },
    dialogue: { label: 'Dialogue', desc: 'Lines and subtext', icon: 'dialogue' },
  },
};

const BOARD_SECTIONS_BY_LOCALE = {
  zh: [
    ['world_state', '世界观', '题材、规则与环境'],
    ['theme', '主题', '命题与表达方向'],
    ['character', '人物', '主角、配角与动机'],
    ['arc_milestone', '人物弧光', '关键转变节点'],
    ['scene', '场景', '可拍摄的戏剧单元'],
    ['event', '事件', '动作、转折与推进'],
    ['plot_device', '戏剧装置', '伏笔、误导与回收'],
    ['dialogue', '对白', '台词、潜台词与情绪'],
  ],
  en: [
    ['world_state', 'World', 'Premise, rules, and setting'],
    ['theme', 'Theme', 'Theme and point of view'],
    ['character', 'Character', 'Leads, supporting roles, and motives'],
    ['arc_milestone', 'Arc', 'Key dramatic shifts'],
    ['scene', 'Scene', 'Shootable dramatic units'],
    ['event', 'Event', 'Actions and reversals'],
    ['plot_device', 'Device', 'Foreshadowing, misdirection, payoff'],
    ['dialogue', 'Dialogue', 'Lines, subtext, and emotion'],
  ],
} as const;

export const BRAND_FACETS_BY_LOCALE: Record<
  Locale,
  Array<{ icon: StudioIconName; label: string; note: string }>
> = {
  zh: [
    {
      icon: 'arc',
      label: '人物弧线',
      note: '跟踪角色的目标、裂缝与转变路径，让每次选择都能留下痕迹。',
    },
    {
      icon: 'scene',
      label: '场景节奏',
      note: '用场与场之间的推进、停顿和回收，控制张力升降与信息释放。',
    },
    {
      icon: 'dialogue',
      label: '对白质感',
      note: '观察潜台词、语气层次和说话方式，让人物声音真正区分开来。',
    },
  ],
  en: [
    {
      icon: 'arc',
      label: 'Character arcs',
      note: 'Track goals, fractures, and shifts so every choice leaves a visible mark on the story.',
    },
    {
      icon: 'scene',
      label: 'Scene rhythm',
      note: 'Control tension through propulsion, pause, and payoff across the sequence of scenes.',
    },
    {
      icon: 'dialogue',
      label: 'Dialogue texture',
      note: 'Shape subtext, tonal layers, and speaking patterns so each voice feels distinct.',
    },
  ],
};

export const BRAND_SURFACES_BY_LOCALE: Record<
  Locale,
  Array<{ icon: StudioIconName; label: string; note: string }>
> = {
  zh: [
    {
      icon: 'structure',
      label: '结构',
      note: '把幕、集、场、转折与回收放进同一张图里，看清叙事骨架怎么支撑全局。',
    },
    {
      icon: 'character',
      label: '人物',
      note: '把目标、关系、裂缝与人物弧线并排整理，避免角色只剩功能分工。',
    },
    {
      icon: 'spark',
      label: '情绪',
      note: '让场景温度、冲突压强和潜台词走向被连续标记，不再只靠感觉记忆。',
    },
    {
      icon: 'lanes',
      label: '节奏',
      note: '把推进、停顿、爆发与回落排成序列，直接检查一整段叙事呼吸。',
    },
  ],
  en: [
    {
      icon: 'structure',
      label: 'Structure',
      note: 'Align acts, episodes, scenes, reversals, and payoffs on one surface so the narrative skeleton stays legible.',
    },
    {
      icon: 'character',
      label: 'Character',
      note: 'Arrange goals, relationships, fractures, and arcs side by side so roles never collapse into pure function.',
    },
    {
      icon: 'spark',
      label: 'Emotion',
      note: 'Mark scene temperature, conflict pressure, and subtextual drift continuously instead of keeping them in memory.',
    },
    {
      icon: 'lanes',
      label: 'Rhythm',
      note: 'Sequence propulsion, pause, eruption, and recovery so the dramatic breathing of a passage can be inspected directly.',
    },
  ],
};

export const APP_TEXT = {
  zh: {
    title: '剧本生成工作台',
    brandEyebrow: '蓦浮叙事工作室',
    brandDescription: '把人物、结构、场景和对白放进同一个创作界面里，像编辑台一样清楚，也像排练场一样可反复推演。',
    brandNote: '让结构、人物、情绪和节奏在同一块画布上被看见、比较和整理。',
    languageLabel: '界面语言',
    languageChinese: '中文',
    languageEnglish: 'English',
    defaultPrompt: '生成一个结构完整、冲突清晰、适合短剧拍摄的版本。',
    loading: '正在加载...',
    accountEyebrow: '账户',
    accountLoggedInFallback: '已登录',
    accountLoggedOutTitle: '访问码登录',
    accountCopyLoggedIn: (provider: string) =>
      `当前账户类型：${
        provider === 'wechat' ? '微信绑定' : provider === 'invite_code' ? '访问码工作区' : '独立工作区'
      }。生成过程、剧本和快照会保存到你的独立工作区。`,
    accountCopyLoggedOut: '输入访问码后即可进入对应工作区，系统会保存剧本和修改记录。',
    inviteCodeLabel: (label: string) => label === '内测码' ? '访问码' : (label || '访问码'),
    inviteCodePlaceholder: (label: string) => `输入${label === '内测码' ? '访问码' : (label || '访问码')}`,
    inviteCodeHint: (label: string) =>
      `使用${label === '内测码' ? '访问码' : (label || '访问码')}登录后，可继续编辑对应工作区中的内容。`,
    wechatLogin: '微信登录',
    guestLogin: '访问码登录',
    wechatHint: '请使用访问码登录。',
    recentRuns: (count: number) => `${count} 条近期生成记录`,
    logout: '退出登录',
    refreshCanvas: '刷新工作区',
    loadExistingScript: '载入已有剧本',
    downloadSnapshot: '下载快照',
    downloadScript: '下载剧本',
    generateStructure: '生成结构',
    generateScript: '生成剧本',
    processing: '处理中',
    ready: '已就绪',
    elementCount: (count: number) => `${count} 个元素`,
    connectionCount: (count: number) => `${count} 条关系`,
    modelingEyebrow: '建模',
    createElement: '创建元素',
    searchEyebrow: '搜索',
    filterView: '过滤视图',
    searchPlaceholder: '搜索人物、场景、主题...',
    leftNote: '固定侧栏里只保留高频操作，把复杂动作放进弹层，避免主工作区被挤乱。',
    storyEyebrow: '故事界面',
    storyBoard: '故事板',
    boardSummaryBusy: '正在同步...',
    boardSummaryReady: '卡片、侧栏和弹层互相独立，不会重叠。',
    scriptEyebrow: '剧本',
    scriptResult: '剧本结果',
    downloadTxt: '下载 TXT',
    downloadJson: '下载 JSON',
    sceneCount: (count: number) => `${count} 场`,
    eventCount: (count: number) => `${count} 个事件`,
    dialogueCount: (count: number) => `${count} 条对白`,
    storyFallback: '后端已返回结果，但当前没有可展示的剧本文本。',
    inspectorEyebrow: '检查器',
    inspectorEmptyTitle: '选择一个元素',
    inspectorEmptyCopy: '点击中间故事板里的任意卡片，这里会显示名称、原始数据和关联信息。',
    nameLabel: '名称',
    jsonLabel: 'JSON 数据',
    save: '保存',
    reset: '重置',
    delete: '删除',
    noRelations: '当前元素还没有关系记录。',
    noRelationDescription: '暂无关系说明。',
    structureEyebrow: '结构助手',
    close: '关闭',
    worldviewLabel: '世界观 / 题材',
    worldviewPlaceholder: '输入题材、时代背景、基调和主要冲突方向。',
    themeLabel: '主题关键词',
    themePlaceholder: '可按行或逗号输入，例如：复仇、家庭、真相、代价。',
    cancel: '取消',
    startGenerate: '开始生成',
    writerEyebrow: '写作助手',
    writingInstruction: '写作指令',
    writingPlaceholder: '告诉模型你想要的风格、长度、冲突密度和目标受众。',
    createItemPending: (label: string) => `正在创建 ${label}...`,
    createItemName: (label: string) => `新${label}`,
    createItemDone: (label: string) => `${label} 已创建`,
    unknownError: '未知错误',
    loadedLatestSnapshot: (name: string) => `已载入最新快照：${name}`,
    loadedBackendFallback: '未找到快照，已载入当前后端状态。',
    snapshotFallbackError: '快照加载失败，已回退到当前后端状态。',
    loadedScript: '已载入已有剧本',
    noScriptBody: '当前没有可载入的剧本文本。',
    noSavedScript: '当前后端还没有现成剧本可载入。',
    scriptDownloadStarted: '剧本下载已开始。',
    snapshotDownloadStarted: '快照下载已开始。',
    downloadFailed: (message: string) => `下载失败：${message}`,
    guestLoginSuccess: '登录成功，内容将保存到对应工作区。',
    loginFailed: (message: string) => `登录失败：${message}`,
    logoutSuccess: '已退出登录，当前切回共享工作区。',
    logoutFailed: (message: string) => `退出失败：${message}`,
    saveSuccess: '元素已保存',
    saveFailed: (message: string) => message || '保存失败',
    deleteConfirm: (name: string) => `确定删除“${name}”吗？`,
    deleteSuccess: '元素已删除',
    deleteFailed: (message: string) => message || '删除失败',
    structurePending: '正在生成结构...',
    structureSuccess: '结构生成完成',
    structureFailed: (message: string) => `结构生成失败：${message}`,
    scriptPending: '正在生成剧本...',
    scriptSuccess: '剧本生成完成',
    scriptFailed: (message: string) => `剧本生成失败：${message}`,
    unnamedElement: '元素',
    saveWorkspace: '保存工作区',
    saveWorkspacePending: '正在保存当前工作区...',
    saveWorkspaceSuccess: '工作区和快照已保存',
    saveWorkspaceFailed: (message: string) => `工作区保存失败：${message}`,
    unsavedChanges: '蝴蝶效应进行中',
    applyDraftSuccess: '修改已写入本地草稿，蝴蝶效应已开始传播；点击“保存工作区”后才会提交到后端。',
    saveBeforeGenerate: '请先保存当前草稿，再进行结构或剧本生成。',
    autoSaveBeforeGenerate: '生成前会自动保存当前草稿，并在完成后自动同步最新结构与剧本结果。',
    workspaceReload: '从服务器重载',
    saveBeforeRefresh: '当前有未保存修改，刷新会丢失本地草稿。继续吗？',
  },
  en: {
    title: 'Script Generation Studio',
    brandEyebrow: 'Morpho Narrative Studio',
    brandDescription:
      'Shape characters, structure, scenes, and dialogue inside one disciplined workspace that feels part editorial desk, part rehearsal floor.',
    brandNote: 'Let structure, character, emotion, and rhythm be seen, compared, and arranged on the same surface.',
    languageLabel: 'Interface language',
    languageChinese: '中文',
    languageEnglish: 'English',
    defaultPrompt: 'Generate a structurally coherent version with clear conflict and strong short-form staging potential.',
    loading: 'Loading...',
    accountEyebrow: 'Account',
    accountLoggedInFallback: 'Signed in',
    accountLoggedOutTitle: 'Invite-code sign in',
    accountCopyLoggedIn: (provider: string) =>
      `Current account type: ${
        provider === 'wechat' ? 'WeChat linked' : provider === 'invite_code' ? 'invite-code workspace' : 'private workspace'
      }. Generation traces, scripts, and snapshots will be stored in your own workspace.`,
    accountCopyLoggedOut:
      'Enter an invite code to access its workspace. Generation traces, scripts, and snapshots will be stored there.',
    inviteCodeLabel: (label: string) => label || 'Invite code',
    inviteCodePlaceholder: (label: string) => `Enter ${label || 'invite code'}`,
    inviteCodeHint: (label: string) =>
      `Use the ${label || 'invite code'} as the login credential. Each code maps to one fixed workspace and can be used to sign back in later.`,
    wechatLogin: 'Sign in with WeChat',
    guestLogin: 'Sign in with invite code',
    wechatHint: 'Please sign in with an access code.',
    recentRuns: (count: number) => `${count} recent runs`,
    logout: 'Sign out',
    refreshCanvas: 'Refresh workspace',
    loadExistingScript: 'Load saved script',
    downloadSnapshot: 'Download snapshot',
    downloadScript: 'Download script',
    generateStructure: 'Generate structure',
    generateScript: 'Generate script',
    processing: 'Working',
    ready: 'Ready',
    elementCount: (count: number) => `${count} elements`,
    connectionCount: (count: number) => `${count} links`,
    modelingEyebrow: 'Modeling',
    createElement: 'Create elements',
    searchEyebrow: 'Search',
    filterView: 'Filter view',
    searchPlaceholder: 'Search characters, scenes, themes...',
    leftNote: 'Keep only high-frequency actions in the fixed sidebar and move heavier actions into modals so the main workspace stays clean.',
    storyEyebrow: 'Story Surface',
    storyBoard: 'Board',
    boardSummaryBusy: 'Syncing...',
    boardSummaryReady: 'Cards, sidebars, and modals stay separated without overlap.',
    scriptEyebrow: 'Script',
    scriptResult: 'Generated script',
    downloadTxt: 'Download TXT',
    downloadJson: 'Download JSON',
    sceneCount: (count: number) => `${count} scenes`,
    eventCount: (count: number) => `${count} events`,
    dialogueCount: (count: number) => `${count} dialogue lines`,
    storyFallback: 'The backend returned a result, but there is no script body to display.',
    inspectorEyebrow: 'Inspector',
    inspectorEmptyTitle: 'Select an element',
    inspectorEmptyCopy: 'Click any card in the board to inspect its title, raw payload, and relationships here.',
    nameLabel: 'Name',
    jsonLabel: 'JSON payload',
    save: 'Save',
    reset: 'Reset',
    delete: 'Delete',
    noRelations: 'This element does not have any recorded links yet.',
    noRelationDescription: 'No relationship description yet.',
    structureEyebrow: 'Composer',
    close: 'Close',
    worldviewLabel: 'World / premise',
    worldviewPlaceholder: 'Describe genre, era, tone, and the main conflict direction.',
    themeLabel: 'Theme keywords',
    themePlaceholder: 'Use lines or commas, for example: revenge, family, truth, cost.',
    cancel: 'Cancel',
    startGenerate: 'Start generation',
    writerEyebrow: 'Writer',
    writingInstruction: 'Writing prompt',
    writingPlaceholder: 'Tell the model about style, length, conflict density, and target audience.',
    createItemPending: (label: string) => `Creating ${label}...`,
    createItemName: (label: string) => `New ${label}`,
    createItemDone: (label: string) => `${label} created`,
    unknownError: 'Unknown error',
    loadedLatestSnapshot: (name: string) => `Loaded latest snapshot: ${name}`,
    loadedBackendFallback: 'No snapshot was found. Loaded the current backend state instead.',
    snapshotFallbackError: 'Snapshot loading failed. Fell back to the current backend state.',
    loadedScript: 'Loaded a saved script',
    noScriptBody: 'There is no saved script body to load right now.',
    noSavedScript: 'The backend does not have a saved script yet.',
    scriptDownloadStarted: 'Script download started.',
    snapshotDownloadStarted: 'Snapshot download started.',
    downloadFailed: (message: string) => `Download failed: ${message}`,
    guestLoginSuccess: 'Signed in with an invite code. Future generations and snapshots will be saved to that workspace.',
    loginFailed: (message: string) => `Sign-in failed: ${message}`,
    logoutSuccess: 'Signed out. Switched back to the shared workspace.',
    logoutFailed: (message: string) => `Sign-out failed: ${message}`,
    saveSuccess: 'Element saved',
    saveFailed: (message: string) => message || 'Save failed',
    deleteConfirm: (name: string) => `Delete "${name}"?`,
    deleteSuccess: 'Element deleted',
    deleteFailed: (message: string) => message || 'Delete failed',
    structurePending: 'Generating structure...',
    structureSuccess: 'Structure generation completed',
    structureFailed: (message: string) => `Structure generation failed: ${message}`,
    scriptPending: 'Generating script...',
    scriptSuccess: 'Script generation completed',
    scriptFailed: (message: string) => `Script generation failed: ${message}`,
    unnamedElement: 'Element',
    saveWorkspace: 'Save workspace',
    saveWorkspacePending: 'Saving current workspace...',
    saveWorkspaceSuccess: 'Workspace and snapshot saved',
    saveWorkspaceFailed: (message: string) => `Workspace save failed: ${message}`,
    unsavedChanges: 'Butterfly effect active',
    applyDraftSuccess: 'Changes were written to the local draft. The butterfly effect is now propagating; use "Save workspace" to commit it.',
    saveBeforeGenerate: 'Save the current draft before running structure or script generation.',
    autoSaveBeforeGenerate: 'Generation will auto-save the current draft and sync the latest structure or script when the run finishes.',
    workspaceReload: 'Reload from server',
    saveBeforeRefresh: 'There are unsaved local changes. Refreshing will discard them. Continue?',
  },
} as const;

export const ASSISTANT_TEXT = {
  zh: {
    eyebrow: '助手',
    title: 'AI 助手',
    intro: '从人物关系、暗线、节奏和对白切口给出更偏创作层面的建议。',
    chars: (count: number) => `${count} 字符`,
    types: [
      ['general', '通用建议'],
      ['plot_twist', '剧情转折'],
      ['character_development', '人物推进'],
      ['dialogue_improvement', '对白优化'],
      ['theme_exploration', '主题深化'],
      ['world_building', '世界观补强'],
    ],
    quick: [
      '给我三个更有戏剧张力的转折点。',
      '指出当前结构里最弱的一场戏。',
      '补充一个能抬高情绪张力的对白方向。',
      '告诉我主角目标和障碍哪里还不够清楚。',
    ],
    requestLead: '用户补充指令：',
    defaultPrompt: '请基于当前故事状态给出建议。',
    replyLanguageHint: '请用中文回答。',
    noContent: 'AI 暂时没有返回内容。',
    unavailable: 'AI 助手暂时不可用，请稍后再试。',
    placeholder: '补充你现在最关心的问题，例如：这条人物线为什么还不够有力？',
    run: '生成建议',
    running: '思考中...',
  },
  en: {
    eyebrow: 'Assistant',
    title: 'AI Assistant',
    intro: 'Get craft-focused notes on relationships, hidden lines, pacing, and dialogue.',
    chars: (count: number) => `${count} chars`,
    types: [
      ['general', 'General notes'],
      ['plot_twist', 'Plot twist'],
      ['character_development', 'Character arc'],
      ['dialogue_improvement', 'Dialogue pass'],
      ['theme_exploration', 'Theme depth'],
      ['world_building', 'Worldbuilding'],
    ],
    quick: [
      'Give me three stronger dramatic reversals.',
      'Point out the weakest scene in the current structure.',
      'Suggest one dialogue direction that raises emotional tension.',
      'Tell me where the protagonist goal or obstacle is still unclear.',
    ],
    requestLead: 'Extra instruction from the user:',
    defaultPrompt: 'Please suggest improvements based on the current story state.',
    replyLanguageHint: 'Please answer in English.',
    noContent: 'AI returned no content.',
    unavailable: 'The AI assistant is temporarily unavailable. Please try again later.',
    placeholder: 'Add the question you care about most right now, for example: why does this character arc still feel weak?',
    run: 'Generate suggestions',
    running: 'Thinking...',
  },
} as const;

export const BOARD_TEXT = {
  zh: {
    emptyTitle: '还没有元素',
    emptyBody: '先从左侧创建人物、主题或场景，或者直接生成结构。新界面会自动分区，不再出现互相重叠的问题。',
    emptySection: '当前分区还没有内容。',
    relationCount: (count: number) => `${count} 条关系`,
    fallbackDetail: '点击后在右侧查看和编辑原始数据。',
  },
  en: {
    emptyTitle: 'No elements yet',
    emptyBody: 'Start by creating characters, themes, or scenes on the left, or generate a structure directly. The new layout keeps every zone separated without overlap.',
    emptySection: 'Nothing in this section yet.',
    relationCount: (count: number) => `${count} links`,
    fallbackDetail: 'Click to inspect and edit the raw payload on the right.',
  },
} as const;

export function resolveInitialLocale(): Locale {
  if (typeof window === 'undefined') {
    return 'zh';
  }

  const requested = new URLSearchParams(window.location.search).get('lang');
  if (requested === 'zh' || requested === 'en') {
    return requested;
  }

  const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (saved === 'zh' || saved === 'en') {
    return saved;
  }

  return 'zh';
}

export function persistLocale(locale: Locale) {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}

export function getBoardSections(locale: Locale) {
  return BOARD_SECTIONS_BY_LOCALE[locale].map(([type, label, note]) => ({
    type,
    label,
    note,
    icon: SECTION_STYLE[type].icon,
    accent: SECTION_STYLE[type].accent,
    tint: SECTION_STYLE[type].tint,
  }));
}
